import { rgba } from 'polished';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Heading, Paragraph, QuillText, tokens } from '@treams/treams-ui';

const MiniCompetencyLevelContainer = styled(Box)<{ $color: string; $active: boolean }>`
	width: 16px;
	height: 16px;
	background: ${(props) => rgba(props.theme.colors[props.$color], props.$active ? 1 : 0.25)};
	border: 1px solid transparent;
	border-radius: 8px;
	cursor: pointer;

	:before {
		display: block;
		content: '';
		width: 32px;
		height: 32px;
		position: absolute;
	}
`;

const MiniCompetencyLevelDot = styled(Box)<{ $color: string; $active: boolean }>`
	margin: 3px;
	width: 8px;
	height: 8px;
	border-radius: 4px;
	background: ${(props) =>
		props.$active ? props.theme.colors.white : props.theme.colors[props.$color]};
	cursor: pointer;
`;

export function MiniCompetencyLevel(props: {
	color: string;
	active: boolean;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
}) {
	return (
		<MiniCompetencyLevelContainer
			$active={props.active}
			$color={props.color}
			onMouseLeave={() => props.onMouseLeave?.()}
			onMouseEnter={() => props.onMouseEnter?.()}
		>
			<MiniCompetencyLevelDot $active={props.active} $color={props.color} />
		</MiniCompetencyLevelContainer>
	);
}

function MiniCompetencyBar(props: {
	competency: graphql.Competency;
	options: graphql.CompetencyLevelOption[];
	levels: (boolean | null)[];
	onSelectLevel?: (option: graphql.CompetencyLevelOption) => void;
	min?: number;
}) {
	const isInteractive = !!props.onSelectLevel;

	const [selectedLevelIndex, setSelectedLevelIndex] = useState<number | undefined>(
		isInteractive ? props.min : undefined,
	);

	return (
		<Flex
			flexGrow={0}
			flexShrink={0}
			onMouseLeave={() => {
				if (isInteractive && props.min) {
					setSelectedLevelIndex(props.min);
					props.onSelectLevel?.(props.options[props.min]);
				}
			}}
		>
			{props.levels.map((level, i) => (
				<Box key={i} mr={i !== props.levels.length - 1 ? 2 : 0}>
					<MiniCompetencyLevel
						color={level === true ? 'green' : level === false ? 'yellow' : 'slateBlue'}
						active={selectedLevelIndex === i}
						onMouseEnter={() => {
							if (isInteractive) {
								setSelectedLevelIndex(i);
								props.onSelectLevel?.(props.options[i]);
							}
						}}
					/>
				</Box>
			))}
		</Flex>
	);
}

const CompetencyElement = styled(Box)`
	background-color: ${tokens.surface.secondary};
	border-radius: 5px;
`;

export default function Competency(props: {
	competency: graphql.Competency;
	min: number;
	options: graphql.CompetencyLevelOption[];
	levels: (boolean | null)[];
	owned?: boolean;
}) {
	const { localize } = useLocalize();

	return (
		<CompetencyElement mt={3} p={4}>
			<Flex alignItems="center">
				<Heading flexGrow={1} flexShrink={1}>
					{localize(props.competency.title).text}
				</Heading>
			</Flex>
			<Paragraph color="slateBlue">
				{props.competency.description &&
					!QuillText.isEmpty(localize(props.competency.description).text) && (
						<QuillText value={localize(props.competency.description).text} />
					)}
			</Paragraph>
			<Box mt={3}>
				<CompetencyLevel
					competency={props.competency}
					options={props.options}
					levels={props.levels}
					level={props.competency.levels[props.min]}
					min={props.min}
					owned={props.owned}
				/>
			</Box>
		</CompetencyElement>
	);
}

function CompetencyLevel(props: {
	competency: graphql.Competency;
	level: graphql.CompetencyLevelOption;
	levels: (boolean | null)[];
	options: graphql.CompetencyLevelOption[];
	min: number;
	owned?: boolean;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const [selectedLevel, setSelectedLevel] = useState<graphql.CompetencyLevelOption>(props.level);

	return (
		<Box>
			<Flex justifyContent="space-between">
				<Paragraph>
					<strong>
						{props.owned && selectedLevel.level.id === props.options[props.min].level.id
							? t('skills.competency.currentLevelCriteria.heading')
							: t('skills.competency.levelCriteria.heading')}{' '}
						&mdash; {localize(selectedLevel.level.title).text}
					</strong>
				</Paragraph>
				<Box mt="4px" flexShrink={0}>
					<MiniCompetencyBar
						competency={props.competency}
						levels={props.levels}
						options={props.options}
						onSelectLevel={(newLevel) => setSelectedLevel(newLevel)}
						min={props.min}
					/>
				</Box>
			</Flex>

			{selectedLevel.criteria ? (
				<Paragraph color="slateBlue">
					<QuillText value={localize(selectedLevel.criteria).text} />{' '}
				</Paragraph>
			) : null}
		</Box>
	);
}
